import { type FC, Fragment } from 'react';
import {
  TJobStatus,
  TObjectJobSummary,
  TOperationType,
} from 'types/objectJob.type';
import { Icon, Tag, Tooltip } from '@timeedit/ui-components';
import { Progress } from '@timeedit/ui-components/antd';
import type { TooltipPlacement } from '@timeedit/ui-components/antd/tooltip/index';

type JobSummary = TObjectJobSummary['summary'];

const getProcessedCount = (summary: JobSummary): number => {
  const unchanged = 'unchanged' in summary ? summary.unchanged : 0;
  return summary.successful + summary.failed + (unchanged ?? 0);
};

const toPercent = (summary: JobSummary) => {
  if (!summary.total) {
    return 0;
  }

  return (getProcessedCount(summary) / summary.total) * 100;
};

const formatCount = (total: number, done: number) => `${done}/${total} objects`;

const failureLabels = (summary: JobSummary, aggregate: string): string[] => {
  if (summary.failed === 0) {
    return [];
  }

  if (!('failedValidation' in summary || 'failedImport' in summary))
    return [aggregate];

  const validationFailures = summary.failedValidation ?? 0;
  const importFailures = summary.failedImport ?? 0;

  if (validationFailures + importFailures !== summary.failed) {
    return [aggregate];
  }

  return [
    ...(validationFailures > 0
      ? [`${validationFailures} failed validation`]
      : []),
    ...(importFailures > 0 ? [`${importFailures} failed import`] : []),
  ];
};

type StatusShellProps = {
  status: TJobStatus;
  tooltipPlacement?: TooltipPlacement;
  children?: React.ReactNode;
};

export const JobStatusTag: FC<StatusShellProps> = ({
  status,
  tooltipPlacement,
  children,
}: StatusShellProps) => {
  const config = TAG_CONFIG[status];
  const statusTagProps: {
    showIcon: boolean;
    state: 'Warning' | 'Error' | 'Success' | 'New' | 'Processing' | 'Default';
    icon?: React.ReactNode;
  } = {
    showIcon: config.showIcon,
    state: config.state,
  };
  if ('icon' in config) {
    statusTagProps.icon = <Icon id={config.icon} alt="" />;
  }
  return (
    <Tooltip
      className="object-job-status"
      placement={tooltipPlacement}
      title={config.tooltip}
    >
      <Tag.StatusTag {...statusTagProps}>{config.label}</Tag.StatusTag>
      {children}
    </Tooltip>
  );
};

export const JobStatusMessage: FC<{
  status: TJobStatus;
  operationType: TOperationType;
  summary: JobSummary;
}> = (props) => {
  const unchanged = 'unchanged' in props.summary ? props.summary.unchanged : 0;
  switch (props.status) {
    case 'QUEUED':
      return (
        <Progress
          percent={0}
          format={() => formatCount(props.summary.total, 0)}
        />
      );
    case 'RUNNING':
      return (
        <Progress
          percent={toPercent(props.summary)}
          format={() =>
            formatCount(props.summary.total, getProcessedCount(props.summary))
          }
        />
      );
    case 'FINISHED':
      return (
        <span className="object-job-status__message">
          {props.summary.successful} objects{' '}
          {COMPLETED_VERB[props.operationType]}
          {unchanged > 0 && <>, {unchanged} unchanged</>}
          {failureLabels(props.summary, `${props.summary.failed} failed`).map(
            (label) => (
              <Fragment key={label}>
                ,{' '}
                <span className="object-job-status__error-message">
                  {label}
                </span>
              </Fragment>
            )
          )}
        </span>
      );
    case 'FAILED':
      return (
        <span className="object-job-status__message">
          <span className="object-job-status__error-message">
            {failureLabels(
              props.summary,
              `${props.summary.failed} objects failed`
            ).join(', ')}
          </span>
        </span>
      );
  }
};

export const JobStatus = ({
  job,
}: {
  job: {
    status: TJobStatus;
    operationType: TOperationType;
    summary: JobSummary;
  };
}) => {
  return (
    <div className="object-job-status__block">
      <JobStatusTag status={job.status}>
        <JobStatusMessage
          status={job.status}
          operationType={job.operationType}
          summary={job.summary}
        />
      </JobStatusTag>
    </div>
  );
};

const COMPLETED_VERB: Record<TOperationType, string> = {
  EDIT_FIELDS: 'updated',
  EDIT_GENERAL_INFO: 'updated',
  EDIT_MEMBERS_AND_RELATIONS: 'updated',
  EDIT_ORGANIZATIONS: 'updated',
  IMPORT: 'updated',
  EXPORT: 'exported',
  DELETE: 'deleted',
};

const TAG_CONFIG = {
  QUEUED: {
    tooltip: 'The job is in the queue and will start soon.',
    state: 'New',
    label: 'In queue',
    showIcon: false,
  },
  RUNNING: {
    tooltip: 'The job is currently running and processing the objects.',
    state: 'Processing',
    label: 'Processing objects',
    showIcon: true,
  },
  FINISHED: {
    tooltip: 'The job has finished successfully.',
    state: 'Success',
    label: 'Finished',
    showIcon: true,
  },
  FAILED: {
    tooltip:
      'The job has failed. Please check the details for more information.',
    state: 'Error',
    label: 'Failed',
    showIcon: true,
  },
  VALIDATE_QUEUED: {
    tooltip: 'The job is in the queue and will start soon.',
    state: 'Processing',
    label: 'Validating import',
    showIcon: true,
  },
  VALIDATING: {
    tooltip: 'The job is currently validating the import.',
    state: 'Processing',
    label: 'Validating import',
    showIcon: true,
  },
  PENDING_REVIEW: {
    tooltip: 'The file has been validated and is ready for import.',
    state: 'Default',
    label: 'Ready for import',
    icon: 'MinusCircleOutlined' as const,
    showIcon: true,
  },
} as const satisfies Record<TJobStatus, unknown>;
